/**
 * Phase 7 — pure helper to convert a chunk's PDF-point bbox to image-pixel
 * coordinates against a single rendered page row.
 *
 * Chunk bboxes are stored in PDF-point coords (the source PDF page's
 * coordinate system), while the page image was rasterised at a higher
 * resolution (typically 150 DPI so points * (150/72) ≈ pixels).  The
 * highlight overlay needs to translate from points to pixels so the
 * rectangle sits over the correct word(s).
 *
 * Returns ``null`` when any required dimension is missing — caller
 * should skip drawing the highlight rather than rendering a wrong-
 * sized rect.
 */

export interface BboxInputs {
  /** Chunk bbox in PDF points: [x0, y0, x1, y1]. */
  bbox: [number, number, number, number];
  /** PDF page width in points (e.g. 612 for US Letter). */
  pdfWidthPt: number;
  /** PDF page height in points (e.g. 792 for US Letter). */
  pdfHeightPt: number;
  /** Image width in pixels (e.g. 1224 for a 150-DPI render of US Letter). */
  imageWidthPx: number;
  /** Image height in pixels (e.g. 1584 for a 150-DPI render of US Letter). */
  imageHeightPx: number;
}

/** Resulting highlight rect in image-pixel space. */
export interface HighlightRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Compute the image-pixel highlight rect for a chunk bbox.
 *
 * Returns ``null`` when any required dimension is missing or non-positive,
 * or when the bbox is malformed (x1 < x0 or y1 < y0).  Returning ``null``
 * lets the caller skip the overlay; we never want to draw a rect at a
 * guessed position because that misleads the user about where the chunk
 * actually lives.
 */
export function computeHighlightRect(
  inputs: BboxInputs,
): HighlightRect | null {
  const { bbox, pdfWidthPt, pdfHeightPt, imageWidthPx, imageHeightPx } = inputs;
  const [x0, y0, x1, y1] = bbox;
  if (
    !Number.isFinite(pdfWidthPt) ||
    !Number.isFinite(pdfHeightPt) ||
    !Number.isFinite(imageWidthPx) ||
    !Number.isFinite(imageHeightPx)
  ) {
    return null;
  }
  if (
    pdfWidthPt <= 0 ||
    pdfHeightPt <= 0 ||
    imageWidthPx <= 0 ||
    imageHeightPx <= 0
  ) {
    return null;
  }
  if (
    !Number.isFinite(x0) ||
    !Number.isFinite(y0) ||
    !Number.isFinite(x1) ||
    !Number.isFinite(y1)
  ) {
    return null;
  }
  if (x1 < x0 || y1 < y0) return null;
  const sx = imageWidthPx / pdfWidthPt;
  const sy = imageHeightPx / pdfHeightPt;
  return {
    left: x0 * sx,
    top: y0 * sy,
    width: (x1 - x0) * sx,
    height: (y1 - y0) * sy,
  };
}
